import { useState, useRef, useEffect } from 'react';
import { itemApi } from '../../../api/items';
import { posApi } from '../../../api/pos';
import { unwrapResponse } from '../../../api/client';
import ProductSearchModal from './ProductSearchModal';
import type { BasketItem } from '../page';
import { toast } from '../../../components/toast';

interface ScanBasketAreaProps {
  selectedLineIndex: number | null;
  onSelectLine: (index: number | null) => void;
  basketItems: BasketItem[];
  setBasketItems: (items: BasketItem[]) => void;
  onReturnQtyUpdate?: (itemId: number, qty: number) => void;
  onReturnLineRemove?: (itemId: number) => void;
  isReturnMode?: boolean;
}

interface Product {
  id: string;
  code: string;
  name: string;
  category: string;
  price: number;
  stock: number;
  barcode: string;
  sku: string;
  unit: string;
  image: string;
  itemId?: number;
  itemTypeId?: number;
  uomId?: number;
  warehouseId?: number;
  isSerialized?: boolean;
  taxes?: any[];
  discounts?: any[];
  brandId?: number | null;
  manufacturerId?: number | null;
  categoryIds?: number[];
}

const toRateNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getItemVatRate = (item: { tax?: boolean; taxes?: any[] }): number => {
  const taxes = Array.isArray(item?.taxes) ? item.taxes : [];
  const extractedRate = taxes.reduce((sum, tax) => {
    const rate = toRateNumber(
      tax?.rate ??
      tax?.taxRate ??
      tax?.taxAmount ??
      tax?.percentage ??
      tax?.percent ??
      tax?.vatPercentage ??
      tax?.tax?.rate ??
      tax?.amount
    );
    return sum + Math.max(0, rate);
  }, 0);

  if (extractedRate > 0) return extractedRate;
  return 0;
};

const parseDateSafe = (value: unknown): Date | null => {
  if (!value) return null;
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

const getActiveDiscount = (item: { discounts?: any[] }) => {
  const now = new Date();
  const discounts = Array.isArray(item?.discounts) ? item.discounts : [];
  return (
    discounts.find((discount) => {
      const from = parseDateSafe(
        discount?.effectiveFrom ??
          discount?.effectiveFromDate ??
          discount?.startDate ??
          discount?.fromDate ??
          discount?.validFrom
      );
      const to = parseDateSafe(
        discount?.effectiveTo ??
          discount?.effectiveToDate ??
          discount?.endDate ??
          discount?.toDate ??
          discount?.validTo
      );
      if (from && now < from) return false;
      if (to && now > to) return false;
      return true;
    }) ?? null
  );
};

const getDiscountDisplay = (item: { discounts?: any[] }) => {
  const activeDiscount = getActiveDiscount(item);
  if (!activeDiscount) return '-';

  const typeName = String(
    activeDiscount?.discountType?.name ??
      activeDiscount?.discountTypeName ??
      activeDiscount?.amountType?.name ??
      activeDiscount?.amountTypeName ??
      activeDiscount?.type ??
      activeDiscount?.valueType ??
      ''
  ).toLowerCase();
  const typeId = Number(
    activeDiscount?.discountType?.id ??
      activeDiscount?.discountTypeId ??
      activeDiscount?.amountType?.id ??
      activeDiscount?.amountTypeId ??
      -1
  );
  const isPercentage =
    activeDiscount?.isPercentage === true ||
    typeId === 1 ||
    typeName.includes('percent');

  const value = toRateNumber(
    activeDiscount?.percentage ??
      activeDiscount?.percent ??
      activeDiscount?.rate ??
      activeDiscount?.discountPercentage ??
      activeDiscount?.amount ??
      activeDiscount?.discountAmount ??
      activeDiscount?.value
  );

  if (value <= 0) return '-';
  return isPercentage ? `${value}%` : value.toFixed(2).replace(/\.00$/, '');
};

const mapItemToProduct = (item: any): Product => ({
  id: String(item?.id ?? item?.itemId ?? item?.ItemId ?? item?.code ?? item?.itemCode ?? ""),
  code: item?.code ?? item?.itemCode ?? item?.sku ?? item?.barcode ?? "ITEM",
  name: item?.name ?? item?.itemName ?? item?.description ?? "Item",
  category: item?.category?.name ?? item?.categoryName ?? item?.group?.name ?? "general",
  price: Number(item?.sellingPrice ?? item?.salesPrice ?? item?.salePrice ?? item?.unitPrice ?? item?.price ?? 0),
  stock: Number(item?.itemStockQuantity ?? item?.stock ?? item?.availableQuantity ?? item?.qty ?? 0),
  barcode: item?.barcode ?? "",
  sku: item?.sku ?? "",
  unit: item?.baseUOM?.name ?? item?.unit?.name ?? item?.uomName ?? item?.uom ?? "ea",
  image: item?.thumbnail ?? item?.thumbnailImageUrl ?? item?.imageUrl ?? "",
  itemId: Number(item?.id ?? item?.itemId ?? 0) || undefined,
  itemTypeId: Number(item?.itemType?.id ?? item?.itemTypeId ?? 0) || undefined,
  uomId: Number(item?.baseUOM?.id ?? item?.uomId ?? 0) || undefined,
  warehouseId: Number(item?.warehouseId ?? 0) || undefined,
  isSerialized: !!item?.serialized,
  taxes: Array.isArray(item?.salesTaxes) ? item.salesTaxes : [],
  discounts: Array.isArray(item?.salesDiscount) ? item.salesDiscount : [],
  brandId: item?.brand?.id ?? null,
  manufacturerId: item?.manufacturer?.id ?? null,
  categoryIds: Array.isArray(item?.itemCategories)
    ? item.itemCategories.map((c: any) => c?.category?.id).filter(Boolean)
    : [],
});

export default function ScanBasketArea({ selectedLineIndex, onSelectLine, basketItems, setBasketItems, onReturnQtyUpdate, onReturnLineRemove, isReturnMode = false }: ScanBasketAreaProps) {
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleAddProductRef = useRef<(product: Product, quantity?: number) => void>(() => {});
  const globalScanHandlerRef = useRef<(code: string) => Promise<void>>(async () => {});

  const filteredProducts = searchQuery.length >= 1 ? searchResults.slice(0, 8) : [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchQuery.trim().length < 1) {
      setSearchResults([]);
      return;
    }

    const resolveWarehouseId = async () => {
      const storedWarehouseId = localStorage.getItem("warehouseId");
      let warehouseId = storedWarehouseId ? Number(storedWarehouseId) : 0;
      if (warehouseId) return warehouseId;

      const outletRaw = localStorage.getItem("outlet");
      if (outletRaw) {
        try {
          const outlet = JSON.parse(outletRaw);
          warehouseId =
            Number(outlet?.warehouse?.id ?? outlet?.warehouseId ?? 0) || 0;
        } catch {
          warehouseId = 0;
        }
      }
      if (warehouseId) {
        localStorage.setItem("warehouseId", String(warehouseId));
        return warehouseId;
      }

      const outletSessionRaw =
        localStorage.getItem("outletSession") ??
        localStorage.getItem("outletSessionInfo");
      if (outletSessionRaw) {
        try {
          const outletSession = JSON.parse(outletSessionRaw);
          warehouseId =
            Number(
              outletSession?.outlet?.warehouse?.id ??
                outletSession?.outlet?.warehouseId ??
                0
            ) || 0;
        } catch {
          warehouseId = 0;
        }
      }
      if (warehouseId) {
        localStorage.setItem("warehouseId", String(warehouseId));
      }

      if (!warehouseId) {
        let outletId = 0;
        try {
          const outlet = JSON.parse(localStorage.getItem("outlet") || "{}");
          outletId = Number(outlet?.id ?? 0) || 0;
        } catch {
          outletId = 0;
        }
        if (!outletId) {
          try {
            const outletSession = JSON.parse(localStorage.getItem("outletSession") || "{}");
            outletId = Number(outletSession?.outlet?.id ?? 0) || 0;
          } catch {
            outletId = 0;
          }
        }
        if (outletId) {
          try {
            const outletDetailsRes = await posApi.getOutletById(outletId);
            const outletDetails = unwrapResponse<any>(outletDetailsRes);
            warehouseId = Number(outletDetails?.warehouse?.id ?? 0) || 0;
            if (warehouseId) {
              localStorage.setItem("warehouseId", String(warehouseId));
              localStorage.setItem("outlet", JSON.stringify(outletDetails));
            }
          } catch {
            warehouseId = 0;
          }
        }
      }
      return warehouseId;
    };

    const query = searchQuery.trim();
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const warehouseId = await resolveWarehouseId();
        if (!warehouseId) {
          setSearchResults([]);
          setIsSearching(false);
          return;
        }
        const data = await itemApi.searchItems({
          searchValue: query,
          warehouseId,
          categoryId: null,
        });
        if (Array.isArray(data)) {
          setSearchResults(data.map(mapItemToProduct));
        } else {
          setSearchResults([]);
        }
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Keep handleAddProductRef current — uses functional setState to increment qty on duplicate
  useEffect(() => {
    handleAddProductRef.current = (product: Product, quantity: number = 1) => {
      setBasketItems(current => {
        const existingIndex = current.findIndex(item => item.itemId && item.itemId === product.itemId);
        if (existingIndex >= 0) {
          const updated = [...current];
          const ex = updated[existingIndex];
          const addQty = Math.max(1, Math.floor(quantity));
          updated[existingIndex] = { ...ex, qty: ex.qty + addQty, total: ex.price * (ex.qty + addQty) };
          return updated;
        }
        return [...current, {
          line: current.length + 1,
          code: product.code,
          name: product.name,
          qty: quantity,
          price: product.price,
          discount: !!getActiveDiscount(product),
          tax: getItemVatRate(product) > 0,
          total: product.price * quantity,
          image: product.image,
          stock: product.stock,
          itemId: product.itemId,
          itemTypeId: product.itemTypeId,
          uomId: product.uomId,
          warehouseId: product.warehouseId,
          isSerialized: product.isSerialized,
          taxes: product.taxes ?? [],
          discounts: product.discounts ?? [],
          brandId: product.brandId ?? null,
          manufacturerId: product.manufacturerId ?? null,
          categoryIds: product.categoryIds ?? [],
        }];
      });
      setSearchQuery('');
      setShowDropdown(false);
      inputRef.current?.focus();
    };
  }, [setBasketItems]);

  // Global barcode scan handler: direct API lookup, no debounce
  useEffect(() => {
    const resolveWarehouseId = async (): Promise<number> => {
      const storedWarehouseId = localStorage.getItem("warehouseId");
      let warehouseId = storedWarehouseId ? Number(storedWarehouseId) : 0;
      if (warehouseId) return warehouseId;
      const outletRaw = localStorage.getItem("outlet");
      if (outletRaw) {
        try { warehouseId = Number(JSON.parse(outletRaw)?.warehouse?.id ?? 0) || 0; } catch { warehouseId = 0; }
      }
      if (warehouseId) { localStorage.setItem("warehouseId", String(warehouseId)); return warehouseId; }
      const outletSessionRaw = localStorage.getItem("outletSession") ?? localStorage.getItem("outletSessionInfo");
      if (outletSessionRaw) {
        try { warehouseId = Number(JSON.parse(outletSessionRaw)?.outlet?.warehouse?.id ?? 0) || 0; } catch { warehouseId = 0; }
      }
      if (warehouseId) { localStorage.setItem("warehouseId", String(warehouseId)); return warehouseId; }
      let outletId = 0;
      try { outletId = Number(JSON.parse(localStorage.getItem("outlet") || "{}")?.id ?? 0) || 0; } catch { outletId = 0; }
      if (!outletId) {
        try { outletId = Number(JSON.parse(localStorage.getItem("outletSession") || "{}")?.outlet?.id ?? 0) || 0; } catch { outletId = 0; }
      }
      if (outletId) {
        try {
          const outletDetailsRes = await posApi.getOutletById(outletId);
          const outletDetails = unwrapResponse<any>(outletDetailsRes);
          warehouseId = Number(outletDetails?.warehouse?.id ?? 0) || 0;
          if (warehouseId) { localStorage.setItem("warehouseId", String(warehouseId)); localStorage.setItem("outlet", JSON.stringify(outletDetails)); }
        } catch { warehouseId = 0; }
      }
      return warehouseId;
    };

    globalScanHandlerRef.current = async (code: string) => {
      if (isReturnMode) return;
      const q = code.trim();
      if (!q) return;
      try {
        const warehouseId = await resolveWarehouseId();
        if (!warehouseId) return;
        const data = await itemApi.searchItems({ searchValue: q, warehouseId, categoryId: null });
        if (!Array.isArray(data)) return;
        const products = data.map(mapItemToProduct);
        const ql = q.toLowerCase();
        const match = products.find(p => p.code?.toLowerCase() === ql || p.barcode?.toLowerCase() === ql)
          ?? (products.length === 1 ? products[0] : null);
        if (match) handleAddProductRef.current(match);
      } catch { /* silent */ }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Capture barcode scanner keystrokes globally (when no other input/textarea is focused)
  useEffect(() => {
    let buffer = '';
    let lastKeyTime = 0;
    const onWindowKeyDown = (e: KeyboardEvent) => {
      const activeEl = document.activeElement as HTMLElement;
      const tag = activeEl?.tagName?.toLowerCase();
      // If our scan input is focused, let the input handle it normally
      if (activeEl === inputRef.current) return;
      // Skip other focusable inputs so we don't intercept typing elsewhere
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      const now = Date.now();
      if (e.key === 'Enter') {
        if (buffer.length > 0) { globalScanHandlerRef.current(buffer); buffer = ''; }
      } else if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        if (now - lastKeyTime > 300 && buffer.length > 0) buffer = '';
        buffer += e.key;
        lastKeyTime = now;
      }
    };
    window.addEventListener('keydown', onWindowKeyDown);
    return () => window.removeEventListener('keydown', onWindowKeyDown);
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setShowDropdown(value.length >= 2);
    setSelectedIndex(-1);
  };

  const handleAddProduct = (product: Product, quantity: number = 1) => {
    if (isReturnMode) return;
    handleAddProductRef.current(product, quantity);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        if (!showDropdown || filteredProducts.length === 0) return;
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredProducts.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        if (!showDropdown || filteredProducts.length === 0) return;
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (searchQuery.trim()) {
          setShowDropdown(false);
          setSelectedIndex(-1);
          globalScanHandlerRef.current(searchQuery.trim());
          setSearchQuery('');
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleRemoveLine = (index: number) => {
    const item = basketItems[index];
    if (item.isReturnLine) {
      const returnLines = basketItems.filter(i => i.isReturnLine);
      if (returnLines.length <= 1) {
        toast.error('At least one item is required for a return');
        return;
      }
      if (item.itemId != null) onReturnLineRemove?.(item.itemId);
    }
    const newItems = basketItems.filter((_, i) => i !== index);
    const renumbered = newItems.map((item, i) => ({ ...item, line: i + 1 }));
    setBasketItems(renumbered);
    if (selectedLineIndex === index) {
      onSelectLine(null);
    }
  };

  const handleUpdateQuantity = (index: number, newQty: number) => {
    const item = basketItems[index];
    if (item.qty < 0) return;
    const qty = Math.trunc(newQty);
    if (!Number.isFinite(qty) || qty < 1) return;
    if (item.isReturnLine) {
      if (item.originalQty != null && qty > item.originalQty) {
        toast.error(`Cannot refund more than the invoiced quantity (${item.originalQty})`);
        return;
      }
      if (item.itemId != null) onReturnQtyUpdate?.(item.itemId, qty);
    } else if (item.stock != null && qty > item.stock) {
      toast.error(`Only ${item.stock} units available in stock`);
      return;
    }
    const newItems = [...basketItems];
    newItems[index].qty = qty;
    newItems[index].total = newItems[index].price * qty;
    setBasketItems(newItems);
  };

  return (
    <div className="flex-1 flex flex-col bg-white border-l border-r border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <div className="flex gap-2 mb-3">
          <div className="flex-1 relative">
            <i className="ri-barcode-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isReturnMode ? 'Adding items is disabled in return mode' : 'Scan barcode, type SKU, or search item...'}
              disabled={isReturnMode}
              className={`w-full pl-10 pr-4 py-2.5 border-2 rounded-lg text-sm focus:outline-none ${isReturnMode ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' : 'border-gray-300 focus:border-teal-500'}`}
            />
            
            {showDropdown && filteredProducts.length > 0 && (
              <div 
                ref={dropdownRef}
                className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-teal-500 rounded-lg shadow-2xl z-50 max-h-96 overflow-y-auto"
              >
                <div className="p-2">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 border-b border-gray-200 mb-1">
                    {isSearching ? "Searching..." : `${filteredProducts.length} product${filteredProducts.length !== 1 ? 's' : ''} found`}
                  </div>
                  {filteredProducts.map((product, index) => (
                    <button
                      key={product.id}
                      onClick={() => handleAddProduct(product)}
                      disabled={product.stock === 0}
                      className={`w-full p-3 rounded-lg text-left transition-colors flex items-center gap-3 ${
                        product.stock === 0
                          ? 'opacity-50 cursor-not-allowed border-2 border-transparent'
                          : selectedIndex === index
                          ? 'bg-teal-50 border-2 border-teal-500 cursor-pointer'
                          : 'hover:bg-gray-50 border-2 border-transparent cursor-pointer'
                      }`}
                    >
                      <div className="w-12 h-12 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                        {product.image ? (
                          <img 
                            src={product.image} 
                            alt={product.name}
                            className="w-full h-full object-cover object-top"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                            <i className="ri-image-line text-lg"></i>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-mono whitespace-nowrap">
                            {product.code}
                          </span>
                          {product.sku && (
                            <span className="px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-mono whitespace-nowrap">{product.sku}</span>
                          )}
                        </div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-0.5 truncate">
                          {product.name}
                        </h4>
                        <div className="flex items-center gap-3 text-xs">
                          <span className={`whitespace-nowrap font-medium ${product.stock === 0 ? 'text-red-600' : 'text-gray-600'}`}>
                            <i className="ri-stack-line mr-1"></i>
                            {product.stock === 0 ? 'Out of Stock' : `Stock: ${product.stock}`}
                          </span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold text-gray-900 whitespace-nowrap">
                          {product.price.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500 whitespace-nowrap">per {product.unit}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {showDropdown && searchQuery.length >= 1 && filteredProducts.length === 0 && (
              <div 
                ref={dropdownRef}
                className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg z-50"
              >
                <div className="p-4 text-center">
                  {isSearching ? (
                    <>
                      <i className="ri-loader-4-line text-3xl text-gray-300 mb-2"></i>
                      <p className="text-sm text-gray-600">Searching...</p>
                    </>
                  ) : (
                    <>
                      <i className="ri-search-line text-3xl text-gray-300 mb-2"></i>
                      <p className="text-sm text-gray-600">No products found</p>
                      <p className="text-xs text-gray-500 mt-1">Try a different search term</p>
                    </>
                  )}
                </div>
              </div>
            )}
            {!isSearching && showDropdown && searchQuery.length >= 1 && !filteredProducts.length && (
              <div className="mt-2 text-xs text-amber-700">
                If no results appear, make sure an outlet with a warehouse is selected.
              </div>
            )}
          </div>
          {!isReturnMode && (
            <button
              onClick={() => setShowSearchModal(true)}
              className="px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-search-line mr-1.5"></i>
              Search
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="border-b border-gray-200">
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">Line</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">Code</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">Image</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">Description</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 whitespace-nowrap">Qty</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 whitespace-nowrap">Price</th>
              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 whitespace-nowrap">Disc</th>
              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 whitespace-nowrap">VAT %</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 whitespace-nowrap">Total</th>
              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 whitespace-nowrap">Action</th>
            </tr>
          </thead>
          <tbody>
            {basketItems.map((item, index) => {
              const isReturn = item.qty < 0;
              return (
                <tr
                  key={item.line}
                  onClick={() => onSelectLine(index)}
                  className={`border-b transition-colors cursor-pointer ${
                    isReturn
                      ? 'bg-red-50 border-red-100 hover:bg-red-100'
                      : selectedLineIndex === index
                      ? 'bg-teal-50 border-gray-100'
                      : 'border-gray-100 hover:bg-gray-50'
                  }`}
                >
                  <td className="px-3 py-2.5 text-sm whitespace-nowrap">
                    <span className={isReturn ? 'text-red-600 font-semibold' : 'text-gray-900'}>{item.line}</span>
                    {isReturn && <span className="ml-1 text-xs bg-red-100 text-red-700 px-1 rounded">RET</span>}
                  </td>
                  <td className={`px-3 py-2.5 text-sm font-mono whitespace-nowrap ${isReturn ? 'text-red-700' : 'text-gray-700'}`}>{item.code}</td>
                  <td className="px-3 py-2.5">
                    <div className="w-12 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                          <i className="ri-image-line text-lg"></i>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className={`px-3 py-2.5 text-sm ${isReturn ? 'text-red-800 font-medium' : 'text-gray-900'}`}>{item.name}</td>
                  <td className="px-3 py-2.5 text-sm text-right whitespace-nowrap">
                    {isReturn ? (
                      <span className="text-red-700 font-semibold">{item.qty}</span>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateQuantity(index, Math.trunc(item.qty) - 1);
                          }}
                          className="w-6 h-6 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-700 rounded transition-colors cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-subtract-line text-sm"></i>
                        </button>
                        <input
                          type="number"
                          onWheel={(e) => e.currentTarget.blur()}
                          value={Math.trunc(item.qty)}
                          onChange={(e) => handleUpdateQuantity(index, parseInt(e.target.value, 10))}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (['.', ',', '-', '+', 'e', 'E'].includes(e.key)) {
                              e.preventDefault();
                              return;
                            }
                            if (e.key === 'ArrowUp') {
                              e.preventDefault();
                              handleUpdateQuantity(index, Math.trunc(item.qty) + 1);
                            } else if (e.key === 'ArrowDown') {
                              e.preventDefault();
                              handleUpdateQuantity(index, Math.trunc(item.qty) - 1);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-16 px-2 py-1 text-center border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                          step="1"
                          min="1"
                          max={item.stock ?? undefined}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateQuantity(index, Math.trunc(item.qty) + 1);
                          }}
                          className="w-6 h-6 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-700 rounded transition-colors cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-add-line text-sm"></i>
                        </button>
                      </div>
                    )}
                  </td>
                  <td className={`px-3 py-2.5 text-sm text-right whitespace-nowrap ${isReturn ? 'text-red-700' : 'text-gray-900'}`}>{item.price.toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-center text-sm text-gray-900 whitespace-nowrap">
                    {getDiscountDisplay(item)}
                  </td>
                  <td className="px-3 py-2.5 text-center text-sm text-gray-900 whitespace-nowrap">
                    {getItemVatRate(item).toFixed(2).replace(/\.00$/, '')}%
                  </td>
                  <td className={`px-3 py-2.5 text-sm font-semibold text-right whitespace-nowrap ${isReturn ? 'text-red-700' : 'text-gray-900'}`}>
                    {item.total.toFixed(2)}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveLine(index);
                      }}
                      className="w-7 h-7 flex items-center justify-center hover:bg-red-100 text-red-600 rounded transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-base"></i>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showSearchModal && (
        <ProductSearchModal 
          onClose={() => setShowSearchModal(false)}
          onAddProduct={handleAddProduct}
        />
      )}
    </div>
  );
}

